import type { Address, Hex } from "viem";

// ---------------------------------------------------------------------------
// Primitive constants
// ---------------------------------------------------------------------------

export const MAX_UINT256 = 2n ** 256n - 1n;
export const ZERO_ADDRESS =
  "0x0000000000000000000000000000000000000000" as Address;
export const ZERO_BYTES32 =
  "0x0000000000000000000000000000000000000000000000000000000000000000" as Hex;

// ---------------------------------------------------------------------------
// sqrtPriceX96 limits (effectively "no limit" for V3/V4 swaps)
// ---------------------------------------------------------------------------

export const MIN_SQRT_PRICE_LIMIT = 4295128739n + 1n;
export const MAX_SQRT_PRICE_LIMIT =
  1461446703485210103287273052203988822378723970342n - 1n;

// ---------------------------------------------------------------------------
// sqrtPriceX96 initial prices (devenv mock prices)
// ---------------------------------------------------------------------------

/** 1:1 ratio: sqrt(1) * 2^96 */
export const SQRT_PRICE_1_1 = 79228162514264337593543950336n;
/** Stablecoin as token0, WETH as token1: price = 1/2000 */
export const SQRT_PRICE_STABLE_WETH = 1771595571142957166518320255467n;
/** WETH as token0, stablecoin as token1: price = 2000 */
export const SQRT_PRICE_WETH_STABLE = 3543191142285914493284720n;
/** WBTC as token0, WETH as token1: price = 15 (15 ETH per BTC) */
export const SQRT_PRICE_WBTC_WETH = 306731823225498842930880724992n;
/** WETH as token0, WBTC as token1: inverse */
export const SQRT_PRICE_WETH_WBTC = 20482261976833980437376512n;

// ---------------------------------------------------------------------------
// Tick ranges (near full-range, aligned to common tick spacings)
// ---------------------------------------------------------------------------

export const TICK_LOWER = -887220;
export const TICK_UPPER = 887220;

// ---------------------------------------------------------------------------
// Token metadata
// ---------------------------------------------------------------------------

export const TOKEN_DECIMALS: Record<string, number> = {
  WETH: 18,
  USDC: 6,
  USDT: 6,
  DAI: 18,
  WBTC: 8,
};

export const TOKEN_SYMBOLS = ["WETH", "USDC", "USDT", "DAI", "WBTC"] as const;

/** Devenv reference prices in USD (based on seeded pool ratios). */
export const DEVENV_TOKEN_PRICES: Record<string, number> = {
  WETH: 2000,
  USDC: 1,
  USDT: 1,
  DAI: 1,
  WBTC: 30000,
};
export type TokenSymbol = (typeof TOKEN_SYMBOLS)[number];

export const V3_FEE_TIERS = [
  { label: "1 bps (stablecoins)", value: 100, tickSpacing: 1 },
  { label: "5 bps", value: 500, tickSpacing: 10 },
  { label: "30 bps (standard)", value: 3000, tickSpacing: 60 },
  { label: "100 bps (exotic)", value: 10000, tickSpacing: 200 },
] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export { sortTokens } from "@gotts.ai/chain";

/** Fresh deadline 1 hour from now. */
export function deadline(): bigint {
  return BigInt(Math.floor(Date.now() / 1000) + 3600);
}

/**
 * Compute a reasonable sqrtPriceX96 for pool initialization.
 * Uses the same mock prices as the seed script (~2000 USDC/ETH, 15 ETH/BTC).
 */
/**
 * Compute the current tick from sqrtPriceX96.
 * tick = floor(log(price) / log(1.0001)) where price = (sqrtPriceX96 / 2^96)^2
 */
export function tickFromSqrtPriceX96(sqrtPriceX96: bigint): number {
  const Q96 = Number(2n ** 96n);
  const sqrtPrice = Number(sqrtPriceX96) / Q96;
  const price = sqrtPrice * sqrtPrice;
  return Math.floor(Math.log(price) / Math.log(1.0001));
}

/** Align a tick to the nearest lower multiple of the tick spacing. */
export function alignTick(tick: number, spacing: number): number {
  return Math.floor(tick / spacing) * spacing;
}

export function getInitialSqrtPriceX96(
  token0: Address,
  token1: Address,
  tokens: { weth?: Address; wbtc?: Address; WETH?: Address; WBTC?: Address },
): bigint {
  const weth = tokens.weth?.toLowerCase() ?? tokens.WETH?.toLowerCase();
  const wbtc = tokens.wbtc?.toLowerCase() ?? tokens.WBTC?.toLowerCase();
  const t0 = token0.toLowerCase();
  const t1 = token1.toLowerCase();

  const hasWeth = t0 === weth || t1 === weth;
  const hasWbtc = t0 === wbtc || t1 === wbtc;

  if (!hasWeth) {
    // Stable/stable pair (or WBTC/stable — treat as 1:1 for devenv)
    return SQRT_PRICE_1_1;
  }

  if (hasWbtc) {
    const wbtcIsToken0 = t0 === wbtc;
    return wbtcIsToken0 ? SQRT_PRICE_WBTC_WETH : SQRT_PRICE_WETH_WBTC;
  }

  // WETH/stablecoin pair
  const wethIsToken0 = t0 === weth;
  return wethIsToken0 ? SQRT_PRICE_WETH_STABLE : SQRT_PRICE_STABLE_WETH;
}
